/**
 * Karakter önizlemesi: **oyundaki gerçek forma sanatı** + üretilen kafa sprite'ı.
 *
 * Gövde PNG'leri `scripts/assets/build_body_previews.py` ile Unity'nin forma sayfalarından
 * üretilir (kaynak: CharGen_Unity_Core/Assets/Takım Vucutlar). Kafa, kontrattaki pivot
 * (0.5, 0.12) ile boyun noktasına oturur — Unity ile aynı kural (ADR-009); kiosk sprite'a
 * dokunmaz, yalnız yerleştirir.
 *
 * Sanatı olmayan `outfitId` için **`null`** döner; çizili yedek gövdeye düşme kararı çağıranındır
 * (`CharacterFigure` → `FootballCharacterSVG`). Bugün kontrattaki beş formanın da sanatı var, yani
 * yedek yalnız **kontrat dışı kalmış eski kayıtlar** için çalışır. Burada eskiden "NEUTRAL, MILLI"
 * yazıyordu ve üçü birden yanlıştı (#120): NEUTRAL enum'dan çıktı (#27), MILLI'nin sanatı üretildi,
 * yedeğe düşen de bu bileşen değil.
 */

import type { ShowcaseHead } from './api/client'
import geometry from './bodies.generated.json'
import { useRetryingImage } from './RemoteImage'

const CANVAS = geometry.canvas
/** Yedek yerleşim değerleri — gerçekleri karakter başına gelir (`ShowcaseHead`, ADR-018).
 *  Yalnız o veri yokken (eski kayıt) kullanılır. */
const HEAD_PIVOT_Y = 0.1
const HEAD_ASPECT = 1254 / 1504
const REFERENCE_HEAD_PX_WIDTH = 1254
const REFERENCE_PPU = 100
const REFERENCE_WORLD_WIDTH = REFERENCE_HEAD_PX_WIDTH / REFERENCE_PPU
/** 1 dünya birimi kaç kart pikseli. Eski "tuval genişliğinin %68'i" kuralından türetildi ki
 *  görünüm birebir korunsun — ama ölçek artık **tuvalden değil içerikten** geliyor.
 *  Tuval karakter başına değiştiği için (ADR-018) tuvale bakan kural dar tuvalli kafayı
 *  büyütür, geniş tuvalliyi küçültürdü: aynı yüz kartta farklı boyda çıkardı. */
const PX_PER_WORLD_UNIT = (CANVAS * 0.68) / REFERENCE_WORLD_WIDTH
const HEAD_WIDTH = REFERENCE_WORLD_WIDTH * PX_PER_WORLD_UNIT
const HEAD_HEIGHT = HEAD_WIDTH / HEAD_ASPECT
/** Kafanın karttaki azami dünya yüksekliği = referans sprite'ın boyu. Tuval içeriğe göre
 *  kurulunca (ADR-018) kafanın dünya boyu sınırsız kaldı; kanonik ölçekte çizilen uzun saçlı
 *  kafa gövdeyi yutuyordu (#89 — Beril 19.2 birim, referans 15.0). Sığdırma **yüksekliğe**
 *  bakar (genişlik `pivot.x = 0.5` simetrikleştirmesinin boş alanıyla şişik, #90) ve tek
 *  yönlüdür: küçültür ama büyütmez — kutuya büyütmek yüzü şişirirdi. */
const MAX_HEAD_WORLD_HEIGHT = REFERENCE_WORLD_WIDTH / HEAD_ASPECT
/** Sığdırmanın ölçtüğü şey: **boyun noktasının üstünde** kalan boy.
 *
 *  #89'da üçlemeden (kırpma yok · kanonik yüz · sınırlı boy) biri feda edilmiş, kanonik yüz
 *  bırakılmıştı. Feda gereksizmiş: pivotun **altındaki** saç gövdenin üstüne düşer, karttan
 *  yer istemez. Toplam boya bakan kural uzun saçlı misafiri saçı yüzünden cezalandırıyordu —
 *  canlıda Ece'nin yüzü kartta herkesinkinin **%81'i** çizildi ve "kafa gövdeye uymuyor" diye
 *  göründü (#108). Ölçü boyun üstüne çevrilince aynı kafa kanonik ölçekte duruyor ve tepeden
 *  taşma yine imkânsız: `HEADROOM` zaten bu sayıdan hesaplanıyordu, iki ölçü artık aynı. */
const MAX_HEAD_ABOVE_PIVOT = MAX_HEAD_WORLD_HEIGHT * (1 - HEAD_PIVOT_Y)

const BODIES: Record<string, { neckY: number }> = geometry.bodies

/** Kafanın gövde üstünde bıraktığı pay — SVG tuvali gövdeden bu kadar uzun.
 *  Sabit **değil**: boyun noktası sanattan geliyor, sanat değişince pay da değişmeli. Sabit 204
 *  iken ayrık figüre geçişte boyun yukarı çıktı ve kafanın tepesi tuvalin dışında kaldı (#72).
 *  En yüksek boyna (en küçük `neckY`) göre hesaplanır ki beş formanın oranı da aynı kalsın.
 *  **Referans kafadan** hesaplanır, karakterden değil: karakter başına değişse her kartın
 *  viewBox oranı farklı olur ve gridde figürler farklı boylarda görünürdü. Referanstan uzun
 *  bir kafa tepeden taşarsa SVG kırpmaz (`overflow: visible`). */
const HEADROOM = Math.ceil(
  Math.max(
    0,
    HEAD_HEIGHT * (1 - HEAD_PIVOT_Y) -
      Math.min(...Object.values(BODIES).map((b) => b.neckY)) * CANVAS,
  ),
)
const VIEW_H = CANVAS + HEADROOM

/** Gövde sanatının adresi, forma kimliğiyle. Dosyalar `public/`'ten okunmaz, import edilir: Vite
 *  adına içerik hash'i koyar ve sunucu `assets/*`'a bir yıllık `immutable` verir (ADR-021, #147).
 *  Sanat yeniden üretilince adres de değişir; tablet eski formayı önbellekten göstermez. */
const BODY_ART: Record<string, string> = Object.fromEntries(
  Object.entries(
    import.meta.glob<string>('./assets/bodies/*.png', {
      eager: true,
      query: '?url',
      import: 'default',
    }),
  ).map(([path, url]) => [path.slice(path.lastIndexOf('/') + 1, -'.png'.length), url]),
)

export function hasBodyArt(outfitId: string): boolean {
  return outfitId in BODIES && outfitId in BODY_ART
}

export function bodyArtUrl(outfitId: string): string | null {
  return BODY_ART[outfitId] ?? null
}

export function BodySprite({
  outfitId,
  headUrl,
  head,
  size,
  glowColor,
  fill = false,
}: {
  outfitId: string
  headUrl: string | null
  /** Kafanın yerleşim sayıları (`GET /api/events/{id}/characters` → `head`). Yoksa referans. */
  head?: ShowcaseHead | undefined
  size: number
  glowColor: string
  /** `true` → SVG kabını doldurur (viewBox oranı korunur). Kart figürü sabit pikselken
   *  kart büyüdükçe içi boş kalıyordu; ölçü artık kaptan gelir. */
  fill?: boolean
}) {
  const img = useRetryingImage(headUrl)
  const body = BODIES[outfitId]
  const art = BODY_ART[outfitId]
  if (!body || !art) return null

  const neckY = HEADROOM + body.neckY * CANVAS
  // Ölçek sprite'ın **dünya boyundan** gelir (piksel genişliği / PPU), tuval genişliğinden değil;
  // referans yüksekliği aşan kafa bütünüyle sığdırılır (MAX_HEAD_WORLD_HEIGHT, #89).
  const worldW = head ? head.width / head.pixelsPerUnit : REFERENCE_WORLD_WIDTH
  const worldH = head ? head.height / head.pixelsPerUnit : MAX_HEAD_WORLD_HEIGHT
  const pivotY = head?.pivotY ?? HEAD_PIVOT_Y
  const fit = Math.min(1, MAX_HEAD_ABOVE_PIVOT / (worldH * (1 - pivotY)))
  const headW = worldW * fit * PX_PER_WORLD_UNIT
  const headX = (CANVAS - headW) / 2
  // Yükseklik **genişlikten** türer: kare varsayılırsa `meet` kafayı kutuya sığdırmak için
  // küçültür ve ortalar (#70). Oran önce yüklenen görselden, yoksa paketteki ölçüden gelir.
  const headH = headW / (img.aspect ?? (head ? head.width / head.height : HEAD_ASPECT))
  // Boyun noktasının oranı karakter başına (ADR-018); bilinmiyorsa referans.
  const headY = neckY - headH * (1 - pivotY)

  return (
    <svg
      width={fill ? '100%' : size}
      height={fill ? '100%' : size * (VIEW_H / CANVAS)}
      viewBox={`0 0 ${CANVAS} ${VIEW_H}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ overflow: 'visible', filter: `drop-shadow(0 0 10px ${glowColor}55)` }}
    >
      <image href={art} x={0} y={HEADROOM} width={CANVAS} height={CANVAS} />
      {img.src ? (
        <image
          href={img.src}
          x={headX}
          y={headY}
          width={headW}
          height={headH}
          preserveAspectRatio="xMidYMid meet"
        />
      ) : (
        /* Kafa henüz yok: üretim sürüyor */
        <g>
          <circle
            cx={CANVAS / 2}
            cy={neckY - HEAD_WIDTH * 0.42}
            r={HEAD_WIDTH * 0.38}
            fill={`${glowColor}22`}
            stroke={glowColor}
            strokeWidth={5}
            strokeDasharray="14 9"
          />
          <text
            x={CANVAS / 2}
            y={neckY - HEAD_WIDTH * 0.42}
            textAnchor="middle"
            fill={`${glowColor}cc`}
            fontSize={34}
            fontFamily="'JetBrains Mono',monospace"
          >
            AI
          </text>
        </g>
      )}
    </svg>
  )
}
